import type { GridToolbarHelpers } from '@inuxe/prime-odata-grid';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { IExpenseResult, IExpenseTotalsResult } from '../../../models/expense/ExpenseModel';
import { expenseService } from '../../../services/modules/expense/expense.service';
import { getRsValueData } from '../../../utils/rsValueUtils';
import { RootStore } from '../../RootStore';

interface ODataParams {
  $filter?: string;
  $orderby?: string;
  $skip?: number;
  $top?: number;
  $select?: string;
  $count?: boolean;
}

interface GridParams {
  startRow: number;
  endRow: number;
  filterModel: any;
  sortModel: any;
  oDataFilter?: string;
}

export class ExpenseStore {
  rootStore: RootStore;
  expenseList: IExpenseResult[] = [];
  selectedExpense: IExpenseResult | null = null;
  error: string | null = null;
  gridHelpers: GridToolbarHelpers<IExpenseResult> | null = null;
  expenseToDelete: IExpenseResult | null = null;
  selectedUserId: string | null = null;
  pendingTotal = 0;
  approvedTotal = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setGridHelpers = (helpers: GridToolbarHelpers<IExpenseResult>) => {
    this.gridHelpers = helpers;
  };

  setSelectedExpense = (expense: IExpenseResult | null) => {
    this.selectedExpense = expense;
  };

  setExpenseToDelete = (expense: IExpenseResult | null) => {
    this.expenseToDelete = expense;
  };

  setSelectedUserId = (userId: string | null) => {
    this.selectedUserId = userId;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  reset = () => {
    this.selectedExpense = null;
    this.error = null;
    this.expenseToDelete = null;
    this.selectedUserId = null;
    this.pendingTotal = 0;
    this.approvedTotal = 0;
  };

  fetchExpenseTotals = async (userId?: string | null) => {
    const userIdForRequest = userId !== undefined ? userId : this.selectedUserId;

    try {
      const response = await expenseService.getExpenseTotals(userIdForRequest);
      const totals = getRsValueData<IExpenseTotalsResult>(response);

      runInAction(() => {
        if (userIdForRequest !== this.selectedUserId) {
          return;
        }

        if (!totals) {
          this.pendingTotal = 0;
          this.approvedTotal = 0;
          return;
        }

        const raw = totals as IExpenseTotalsResult & {
          PendingTotal?: number;
          ApprovedTotal?: number;
        };

        this.pendingTotal = Number(raw.pendingTotal ?? raw.PendingTotal ?? 0);
        this.approvedTotal = Number(raw.approvedTotal ?? raw.ApprovedTotal ?? 0);
      });
    } catch (error) {
      console.error('Error fetching expense totals:', error);
      runInAction(() => {
        if (userIdForRequest !== this.selectedUserId) {
          return;
        }
        this.pendingTotal = 0;
        this.approvedTotal = 0;
      });
    }
  };

  refreshGrid = () => {
    this.gridHelpers?.refreshGrid();
  };

  reloadTotalsAndGrid = async () => {
    await this.fetchExpenseTotals();
    this.refreshGrid();
  };

  listExpenses = async (
    params: GridParams
  ): Promise<{ rows: IExpenseResult[]; lastRow: number; count: number }> => {
    try {
      const odataParams: ODataParams = {
        $count: true,
        $skip: params.startRow,
        $top: params.endRow - params.startRow,
      };

      const filters: string[] = [];

      // Admin seçili bir kullanıcı seçtiyse onun kayıtlarını göster
      if (this.selectedUserId) {
        filters.push(`userId eq '${this.selectedUserId}'`);
      }

      if (params.oDataFilter) {
        filters.push(params.oDataFilter);
      }

      if (params.filterModel) {
        const filterModelFilters = Object.entries(params.filterModel).map(
          ([field, filter]: [string, any]) => {
            const numericFields = ['id', 'amount'];
            if (numericFields.includes(field)) {
              return `${field} eq ${filter.filter}`;
            }
            const value = String(filter.filter ?? '')
              .toLowerCase()
              .replace(/'/g, "''");
            return `contains(tolower(${field}), '${value}')`;
          }
        );

        filters.push(...filterModelFilters);
      }

      if (filters.length > 0) {
        odataParams.$filter = filters.join(' and ');
      }

      if (params.sortModel && params.sortModel.length > 0) {
        const { colId, sort } = params.sortModel[0];
        odataParams.$orderby = `${colId} ${sort === 'asc' ? 'asc' : 'desc'}`;
      }

      const response: any = await expenseService.listExpenses({
        params: odataParams,
      });

      return runInAction(() => {
        if (response) {
          const totalCount = response['@odata.count'] || 0;
          return {
            rows: response.value || [],
            lastRow: totalCount,
            count: totalCount,
          };
        }
        return {
          rows: [],
          lastRow: 0,
          count: 0,
        };
      });
    } catch (error) {
      console.error('Error fetching expenses:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
      });
      throw error;
    }
  };

  createExpense = async (formData: FormData) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await expenseService.createExpense(formData);

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        toast.error(response.message || 'Harcama kaydı oluşturulamadı');
        return response;
      }

      runInAction(() => {
        this.error = null;
      });
      await this.reloadTotalsAndGrid();

      toast.success('Harcama kaydı başarıyla oluşturuldu');
      return response;
    } catch (error) {
      console.error('Error creating expense:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
        toast.error('Harcama kaydı oluşturulurken bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  updateExpense = async (id: number, formData: FormData) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await expenseService.updateExpense(id, formData);

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        toast.error(response.message || 'Harcama kaydı güncellenemedi');
        return response;
      }

      runInAction(() => {
        this.error = null;
      });
      await this.reloadTotalsAndGrid();

      toast.success('Harcama kaydı başarıyla güncellendi');
      return response;
    } catch (error) {
      console.error('Error updating expense:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
        toast.error('Harcama kaydı güncellenirken bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  deleteExpense = async (id: number) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await expenseService.deleteExpense(id);

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        toast.error(response.message || 'Harcama kaydı silinemedi');
        return response;
      }

      runInAction(() => {
        this.error = null;
      });
      await this.reloadTotalsAndGrid();

      toast.success('Harcama kaydı başarıyla silindi');
      return response;
    } catch (error) {
      console.error('Error deleting expense:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
        toast.error('Harcama kaydı silinirken bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  getExpenseById = async (id: number): Promise<IExpenseResult | null> => {
    try {
      const response: any = await expenseService.getExpenseById(id);
      if (response.error || !response.value) {
        return null;
      }
      return response.value;
    } catch (error) {
      console.error('Error fetching expense by id:', error);
      return null;
    }
  };
}
